import { difference } from "ramda";
import React, { Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useState } from "react";
import { useRateTreeContext } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTreeProvider";

const TreeViewNodeNavigationContext = React.createContext<
  [focusedItem: string | null, setFocusedItem: Dispatch<SetStateAction<string | null>>] | undefined
>(undefined);
TreeViewNodeNavigationContext.displayName = "TreeViewNodeNavigation";

export function TreeViewNodeNavigationProvider({ children }: PropsWithChildren) {
  const [focusedItem, setFocusedItem] = useState<string | null>(null);
  return (
    <TreeViewNodeNavigationContext.Provider value={[focusedItem, setFocusedItem]}>
      {children}
    </TreeViewNodeNavigationContext.Provider>
  );
}

export function useTreeViewNodeNavigation() {
  const { expand, expanded, getParent } = useRateTreeContext();
  const context = useContext(TreeViewNodeNavigationContext);
  if (!context) {
    throw new Error("useTreeViewNodeNavigation must be used within a TreeViewNodeNavigationProvider");
  }
  const [focusedItem, setFocusedItem] = context;

  const navigateToNode = useCallback(
    (nodeId: string) => {
      const pathArr = [];
      let parent: string | undefined = nodeId;
      while ((parent = getParent(parent)?.id)) {
        pathArr.push(parent);
      }
      if (difference(pathArr, expanded).length > 0) {
        expand(pathArr);
      }
      setFocusedItem(nodeId);
    },
    [getParent, expand, setFocusedItem]
  );

  const cancelFocused = useCallback(() => {
    setFocusedItem(null);
  }, [setFocusedItem]);

  return {
    navigateToNode,
    cancelFocused,
    focusedItem,
  };
}
